import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { SpinnerGradient } from '@assets/icons';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { cn } from 'cn';
import { ConditionalTooltip } from '@/components/ui/conditional-tooltip';
const buttonVariants = cva(['relative inline-flex items-center justify-center gap-1 refresh:gap-1.5 whitespace-nowrap font-medium text-sm rounded-lg transition-[box-shadow,border,background] duration-320 ease cursor-pointer', 'ring-offset-background', 'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', 'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none disabled:border-none disabled:shadow-none', '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'], {
  variants: {
    variant: {
      // DEPRECATED
      default: 'bg-primary-900 text-primary-foreground hover:bg-primary-800',
      destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      outline: 'border border-tertiary-300 bg-background text-secondary-800 shadow-sm hover:bg-tertiary-100',
      ghost: 'text-secondary-700 hover:bg-tertiary-150 hover:text-secondary-900',
      link: 'text-primary-700 underline-offset-4 hover:underline',
      beige: 'border border-tertiary-300 bg-tertiary-100 text-secondary-900 hover:bg-tertiary-150',
      green: 'flex flex-row gap-3 bg-primary px-12 py-2 text-primary-foreground shadow-sm transition-colors duration-200 hover:bg-primary-hover',
      quaternary: 'bg-tertiary-150 hover:bg-tertiary-200 text-secondary-800 disabled:bg-tertiary-150 disabled:text-secondary-600 disabled:opacity-50',
      // END DEPRECATED
      // primary-foreground (not white) so text stays readable on a light
      // org brand color. Under refreshed shell, disabled drops to a neutral grey
      // fill so it reads as inactive on any brand color, light or dark.
      primary: 'bg-primary hover:bg-primary-hover text-primary-foreground shadow-sm disabled:text-primary-foreground/50 refresh:disabled:bg-muted refresh:disabled:text-muted-foreground/70 disabled:opacity-100',
      secondary: 'border border-tertiary-300 bg-background text-secondary-800 shadow-sm hover:bg-tertiary-150',
      'secondary-alt': 'bg-tertiary-150 hover:bg-tertiary-200 text-secondary-800 hover:text-secondary-800',
      tertiary: 'text-foreground hover:bg-accent disabled:bg-transparent disabled:text-muted-foreground disabled:opacity-50',
      'half-transparent': 'bg-secondary-900/5 hover:bg-secondary-900/10 text-secondary-600 text-xs leading-4 disabled:bg-secondary-900/5 disabled:text-secondary-600 disabled:opacity-50',
      /**
       * Last-resort escape hatch: strips default button chrome so callers
       * own all visual styling (menu rows, chip bodies, composite targets).
       * Prefer ghost/secondary when possible. Focus ring is preserved.
       */
      unstyled: 'h-auto min-h-0 gap-0 rounded-none border-0 bg-transparent p-0 font-normal text-inherit shadow-none hover:bg-transparent disabled:bg-transparent disabled:shadow-none [&_svg]:size-[1em]'
    },
    size: {
      // DEPRECATED
      default: 'px-3 refresh:px-4 py-2',
      sm: 'px-2 refresh:px-2.5 py-1',
      icon: 'h-10 w-10',
      // END DEPRECATED
      md: 'h-8 px-1.5 refresh:px-3.5 py-1.5',
      lg: 'h-10 p-2.5 refresh:px-5',
      'lg-alt': 'h-10 px-5 py-2.5',
      'icon-sm': 'h-auto w-auto p-0.5'
    }
  },
  compoundVariants: [{
    // Under refreshed shell the button's gap spaces icon and label; drop
    // per-icon margins (mr-1.5 etc.) so they don't stack into an
    // oversized gap. Unstyled callers own their own spacing.
    variant: ['default', 'destructive', 'outline', 'ghost', 'link', 'beige', 'green', 'quaternary', 'primary', 'secondary', 'secondary-alt', 'tertiary', 'half-transparent'],
    class: 'refresh:[&_svg]:mx-0'
  }],
  defaultVariants: {
    variant: 'primary',
    size: 'md'
  }
});
export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
  asChild?: boolean;
  /**
   * Pass a string to disable the button and show the string
   * as a tooltip explaining why. The tooltip is inert while
   * loading — the spinner already explains a pending disable.
   * With `asChild`, the child must enforce its own disabled
   * state — the tooltip does not block interaction.
   */
  disabled?: boolean | string;
  /**
   * Classes for the tooltip wrapper span rendered while
   * disabled with a reason. The span is inline-flex and
   * shrink-wraps; pass 'w-full' for full-width buttons to
   * avoid layout shift when disabled.
   */
  disabledClass?: string;
  ref?: React.Ref<HTMLButtonElement>;
}
const Button = t0 => {
  const $ = _c(33);
  let children;
  let className;
  let disabled;
  let disabledClass;
  let loading;
  let props;
  let ref;
  let size;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      children,
      disabled,
      loading,
      asChild: t1,
      disabledClass,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = disabledClass;
    $[5] = loading;
    $[6] = props;
    $[7] = ref;
    $[8] = size;
    $[9] = t1;
    $[10] = variant;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    disabledClass = $[4];
    loading = $[5];
    props = $[6];
    ref = $[7];
    size = $[8];
    t1 = $[9];
    variant = $[10];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "button";
  const disabledReason = typeof disabled === "string" ? disabled : undefined;
  const t2 = !loading && !!disabledReason;
  let t3;
  if ($[11] !== className || $[12] !== loading || $[13] !== size || $[14] !== variant) {
    t3 = cn(buttonVariants({
      variant,
      size
    }), className, {
      "text-transparent! select-none": loading
    });
    $[11] = className;
    $[12] = loading;
    $[13] = size;
    $[14] = variant;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const t4 = !!disabled || loading;
  let t5;
  if ($[16] !== asChild || $[17] !== children || $[18] !== loading || $[19] !== variant) {
    t5 = asChild ? children : <>{loading && <span className="absolute inset-0 flex items-center justify-center"><SpinnerGradient className="animate-spin" theme={variant === "primary" ? "light" : "dark"} /></span>}{children}</>;
    $[16] = asChild;
    $[17] = children;
    $[18] = loading;
    $[19] = variant;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== Comp || $[22] !== props || $[23] !== ref || $[24] !== t3 || $[25] !== t4 || $[26] !== t5) {
    t6 = <Comp ref={ref} className={t3} disabled={t4} {...props} data-sentry-element="Comp" data-sentry-source-file="button.tsx">{t5}</Comp>;
    $[21] = Comp;
    $[22] = props;
    $[23] = ref;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== disabledClass || $[29] !== disabledReason || $[30] !== t2 || $[31] !== t6) {
    t7 = <ConditionalTooltip enabled={t2} content={disabledReason} triggerClassName={disabledClass} data-sentry-element="ConditionalTooltip" data-sentry-component="Button" data-sentry-source-file="button.tsx">{t6}</ConditionalTooltip>;
    $[28] = disabledClass;
    $[29] = disabledReason;
    $[30] = t2;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  return t7;
};
Button.displayName = 'Button';
export { Button, buttonVariants };